<script setup>
import {debounce, get, kebabCase} from 'lodash';
import Pagination from '@/components/Shared/Pagination.vue';
import {router} from '@inertiajs/vue3';
import {computed, onMounted, ref} from 'vue';
import FilterDropdown from '@/components/Shared/Table/FilterDropdown.vue';

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  noDataText: {
    type: String,
    default: 'No results.',
  },
  items: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object,
    default: null,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  searchPlaceholder: {
    type: String,
    default: null,
  },
  filterFields: {
    type: Array,
    default: null,
  },
  hiddenColumns: {
    type: Array,
    default: null,
  },
  canSelectPerPage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['search-changed']);

const searchInput = ref(null);

onMounted(() => searchInput.value?.focus());

const filteredHeaders = computed(() => props.headers.filter(header => !isHidden(header.value)));

const parseName = text => kebabCase(text);
const textAlignClass = header => ({
  'text-left': header.align === 'start',
  'text-center': header.align === 'center',
  'text-right': header.align === 'end',
});
const onFilterChange = data => {
  const filters = {...props.filters, ...data};
  const queryParams = {};

  Object.keys(filters).forEach(filter => {
    if (filters[filter] !== null && filters[filter] !== '—') {
      queryParams[filter] = filters[filter];
    }
  });

  router.visit(route(route().current(), route().params), {data: queryParams, preserveState: true});
};

const handleSearchChanged = debounce(event => onFilterChange({search: event.target.value, page: null}), 1000);

const handleFilterChanged = debounce(event => onFilterChange({filter: event, page: null}), 1000);

const handleSort = header => {
  if (header.sortable) {
    const sort = props.filters.sort === header.value ? `-${header.value}` : header.value;
    onFilterChange({sort, page: null});
  }
};

const handlePerPageChanged = perPage => onFilterChange({perPage, page: 1});
const isHidden = header => props.hiddenColumns?.includes(header) ?? false;

const getItemValue = (item, key) => {
  let value = get(item, key)

  if (value instanceof Object && value.hasOwnProperty('title') && value.hasOwnProperty('subtitle')) {
    return `<span class="d-block">${value.title}</span><small>${value.subtitle}</small>`
  }

  return value
};
</script>

<template>
  <div>
    <div class="flex gap">
      <div :class="{ 'justify-content-end': !searchable }" class="w-full flex items-center my-2">
        <input
          v-if="searchable"
          ref="searchInput"
          :placeholder="searchPlaceholder || trans('common.search')"
          :value="filters.search"
          class="form_field"
          type="text"
          @keydown="handleSearchChanged"
        />
        <filter-dropdown
          v-if="filterFields"
          :filter-values="typeof filters.filter === 'object' ? filters.filter : {}"
          :filters="filterFields"
          @filter-changed="handleFilterChanged"
        />
      </div>
    </div>
    <div>
      <table aria-describedby="A reusable datatable" class="table w-full">
        <thead>
        <tr>
          <th
            v-for="(header, index) in filteredHeaders"
            :key="`header-${index}`"
            :class="[textAlignClass(header), header.sortable ? 'cursor-pointer' : '']"
            @click="handleSort(header)"
          >
            <slot :name="parseName(`header-${header.value}`)">
              {{ header.text }}
            </slot>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, row) in items" :key="`item-row-${row}`">
          <td v-for="(header, cell) in filteredHeaders" :key="`item-${row}-${cell}`" :class="textAlignClass(header)">
            <slot :name="parseName(`item-${header.value}`)" v-bind="{ item, index: row }">
              <p v-html="getItemValue(item, header.value)"></p>
            </slot>
          </td>
        </tr>
        <tr v-if="!items.length" class="text-center">
          <td :colspan="filteredHeaders.length">{{ noDataText }}</td>
        </tr>
        </tbody>
      </table>
      <pagination
        v-if="pagination"
        :can-select-per-page="canSelectPerPage"
        :pagination="pagination"
        @update:per-page="handlePerPageChanged"
        @paginate="onFilterChange"/>
    </div>
  </div>
</template>
